/* Cart-wide guard for quantity-driven subscription bundles (O8-style offers).
 *
 * Problem: Shopify's /cart/change.js can change a line's quantity but NEVER
 * its selling_plan. If a customer bumps quantity on a line whose plan is
 * "Every 4 Weeks" (1 bottle), the line would stay on that plan at the new
 * quantity instead of snapping to whichever plan covers that many bottles
 * (e.g. "Every 16 Weeks" for 4) — there is no Shopify endpoint that changes
 * quantity and selling_plan in one request, so this must always be a
 * remove-then-re-add, which briefly leaves the cart in an INCOMPLETE state
 * (old line gone, new one not added yet).
 *
 * The live cart is the UpCart app, with its own +/- quantity stepper in the
 * drawer. This file:
 *
 *  1) Intercepts the drawer's +/- and typed quantity changes (capture phase,
 *     before UpCart's own handler) and applies the correct quantity+plan
 *     directly, so UpCart never fires its own request with stale data.
 *
 *  2) Locks checkout for the duration of any remove -> re-add window (the
 *     line briefly doesn't exist at all mid-swap). A checkout attempt during
 *     the lock is queued and fired the instant the cart is consistent again.
 *
 *  3) Runs ONE reconcile sweep on page load as a fallback for surfaces this
 *     file doesn't intercept (e.g. the native /cart page's own input).
 *
 * DELIBERATELY REMOVED (do not re-add): the window.fetch patch that
 * re-triggered a reconcile after every cart request. Combined with the PDP
 * bundle's own add+reconcile flow and the fetch wrappers installed by
 * UpCart / Visually / Convertify, a single Add To Cart click cascaded into
 * 8-12 cart requests and multi-second main-thread stalls. Coordination with
 * the PDP bundle now happens through the shared window.__o8CartLock flag
 * instead: whoever is mutating the cart raises it, the other waits.
 *
 * Scoped by SELLING PLAN NAMING CONVENTION ("Every N Weeks"), not by product
 * handle/id — covers O8 and any future offer built the same way.
 */
(function () {
  const WEEK_PLAN_RE = /(\d+)\s*weeks?\b/i;
  const CHECKOUT_SELECTOR =
    // UpCart's real checkout link is a Shopify cart-permalink
    // (/cart/c/<token>?key=...) — it never contains the string "/checkout".
    // .upcart-checkout-button is the actual stable class from the live
    // drawer; the rest are fallbacks for the native cart page.
    '.upcart-checkout-button, a[href*="/checkout"], a[href*="/cart/c/"], button[name="checkout"], input[name="checkout"], [data-checkout]';

  /* Shared cross-script cart lock. The PDP bundle checks and raises the same
     flag, so the drawer guard and the PDP reconcile never mutate the cart at
     the same time. */
  window.__o8CartLock = window.__o8CartLock || { busy: false };

  function parseWeeks(text) {
    if (!text) return null;
    const m = String(text).match(WEEK_PLAN_RE);
    if (!m) return null;
    const weeks = parseInt(m[1], 10);
    return weeks > 0 && weeks % 4 === 0 ? weeks : null;
  }

  const productPlanCache = new Map();

  async function fetchProductPlans(handle) {
    if (productPlanCache.has(handle)) return productPlanCache.get(handle);
    let plans = [];
    try {
      const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      const product = await res.json();
      (product.selling_plan_groups || []).forEach((group) => {
        (group.selling_plans || []).forEach((plan) => {
          const weeks = parseWeeks(plan.name);
          if (weeks) plans.push({ bottles: weeks / 4, planId: String(plan.id) });
        });
      });
    } catch (err) {
      console.error('[o8-cart-guard] failed to fetch plans for', handle, err);
    }
    productPlanCache.set(handle, plans);
    return plans;
  }

  function snapToPlan(plans, quantity) {
    const sorted = plans.slice().sort((a, b) => a.bottles - b.bottles);
    const maxBottles = sorted[sorted.length - 1].bottles;
    const desired = Math.min(quantity, maxBottles);
    return sorted.find((p) => p.bottles >= desired) || sorted[sorted.length - 1];
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // ---- Checkout lock --------------------------------------------------
  // Blocks navigating to checkout while a remove+re-add is in flight. A
  // checkout attempt during the lock is queued and fired automatically the
  // instant the cart is consistent again.

  let cartBusy = false;
  let pendingCheckoutHref = null;
  let lockOverlay = null;

  function showLock() {
    if (lockOverlay) return;
    lockOverlay = document.createElement('div');
    lockOverlay.setAttribute('data-o8-cart-lock', '');
    lockOverlay.setAttribute('aria-hidden', 'true');
    // pointer-events: none — visual-only cue. If it captured clicks, a click
    // on the real checkout button underneath would hit THIS div instead, and
    // the queued-navigate-on-unlock behavior would silently never fire.
    lockOverlay.style.cssText =
      'position:fixed;inset:0;z-index:2147483647;pointer-events:none;background:rgba(255,255,255,0.01);cursor:wait;';
    document.body.appendChild(lockOverlay);
  }

  function hideLock() {
    if (lockOverlay) {
      lockOverlay.remove();
      lockOverlay = null;
    }
  }

  function setCartBusy(on) {
    cartBusy = on;
    window.__o8CartLock.busy = on;
    if (on) {
      showLock();
      return;
    }
    hideLock();
    if (pendingCheckoutHref) {
      const href = pendingCheckoutHref;
      pendingCheckoutHref = null;
      window.location.href = href;
    }
  }

  function attachCheckoutLock() {
    document.addEventListener(
      'click',
      (evt) => {
        if (!cartBusy) return;
        const target = evt.target.closest(CHECKOUT_SELECTOR);
        if (!target) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        pendingCheckoutHref = target.getAttribute('href') || '/checkout';
      },
      true
    );

    // Some checkout buttons submit the cart <form> directly rather than
    // navigating a plain link.
    document.addEventListener(
      'submit',
      (evt) => {
        if (!cartBusy) return;
        const form = evt.target;
        if (!form || !form.querySelector) return;
        if (!form.querySelector('[name="checkout"]')) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        pendingCheckoutHref = '/checkout';
      },
      true
    );
  }

  // Swap a cart line to the correct plan for `newQty` bottles. Looks the line
  // up fresh from /cart.js (by variant + current selling_plan) to get a real
  // line `key` — the DOM alone doesn't expose one reliably.
  async function applyCorrectQuantity(variantId, oldPlanId, handle, newQty) {
    setCartBusy(true);
    try {
      // Both lookups are independent — run them in parallel.
      const [cart, plans] = await Promise.all([
        fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()),
        newQty > 0 ? fetchProductPlans(handle) : Promise.resolve([]),
      ]);
      const line = (cart.items || []).find(
        (it) =>
          String(it.variant_id) === String(variantId) &&
          it.selling_plan_allocation &&
          String(it.selling_plan_allocation.selling_plan.id) === String(oldPlanId)
      );
      if (!line) return;

      if (newQty <= 0) {
        await postJSON('/cart/change.js', { id: line.key, quantity: 0 });
        return;
      }

      if (!plans.length) return;
      const snapped = snapToPlan(plans, newQty);

      // Already correct for the snapped target — nothing to do.
      if (String(snapped.planId) === String(oldPlanId) && snapped.bottles === line.quantity) return;

      // SEQUENTIAL on purpose. If add were processed before change and
      // consolidated into the existing line, change would then zero out the
      // merged line including the freshly added quantity. The extra round
      // trip is the price of a deterministic result.
      await postJSON('/cart/change.js', { id: line.key, quantity: 0 });
      await postJSON('/cart/add.js', {
        items: [{ id: variantId, quantity: snapped.bottles, selling_plan: snapped.planId }],
      });
    } finally {
      setCartBusy(false);
    }
  }

  // Reads the current line's context straight off the UpCart tile markup:
  // variant + selling_plan come from the product link's query string, the
  // frequency from the visible "Every N Weeks" label, quantity from the
  // stepper's own input.
  function getUpcartLineContext(controlEl) {
    const tile = controlEl.closest('li');
    if (!tile) return null;
    const link = tile.querySelector('a[href*="selling_plan="]');
    if (!link) return null;
    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.origin);
    } catch (err) {
      return null;
    }
    const variantId = url.searchParams.get('variant');
    const sellingPlanId = url.searchParams.get('selling_plan');
    const handle = url.pathname.split('/products/')[1];
    const freqEl = tile.querySelector('.upcart-public-product-properties__subscription');
    const weeks = parseWeeks(freqEl && freqEl.textContent);
    const qtyInput = tile.querySelector('input[aria-label="Product quantity"]');
    const currentQty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : null;
    if (!variantId || !sellingPlanId || !handle || !weeks || currentQty == null) return null;
    return { variantId, sellingPlanId, handle, currentQty };
  }

  async function handleUpcartQtyChange(controlEl, newQty) {
    const ctx = getUpcartLineContext(controlEl);
    if (!ctx) return false; // not one of ours — let UpCart handle it normally
    if (cartBusy || window.__o8CartLock.busy) return true; // mid-update — swallow the extra click
    try {
      await applyCorrectQuantity(ctx.variantId, ctx.sellingPlanId, ctx.handle, newQty);
    } catch (err) {
      console.error('[o8-cart-guard] failed to apply corrected quantity', err);
    }
    return true;
  }

  function attachUpcartInterception() {
    // Capture phase = runs before UpCart's own bubble-phase click handler,
    // regardless of load order.
    document.addEventListener(
      'click',
      (evt) => {
        const plus = evt.target.closest('.upcart-public-component-product-tile__quantity-plus');
        const minus = evt.target.closest('.upcart-public-component-product-tile__quantity-minus');
        const btn = plus || minus;
        if (!btn) return;
        const ctx = getUpcartLineContext(btn);
        if (!ctx) return; // not ours — let UpCart's own handler run
        evt.preventDefault();
        evt.stopImmediatePropagation();
        const delta = plus ? 1 : -1;
        handleUpcartQtyChange(btn, ctx.currentQty + delta);
      },
      true
    );

    // Typed-in quantity (commits on change/blur/enter).
    document.addEventListener(
      'change',
      (evt) => {
        const input = evt.target.closest('input[aria-label="Product quantity"]');
        if (!input) return;
        const ctx = getUpcartLineContext(input);
        if (!ctx) return;
        evt.stopImmediatePropagation();
        const typed = parseInt(input.value, 10);
        handleUpcartQtyChange(input, Number.isFinite(typed) ? typed : ctx.currentQty);
      },
      true
    );
  }

  attachCheckoutLock();
  attachUpcartInterception();

  // ---- Fallback sweep: ONE pass on page load only. Catches lines left in
  // an inconsistent state by surfaces this file doesn't intercept (e.g. the
  // native /cart page's own quantity input, which round-trips through a page
  // reload anyway). Never re-triggered by network activity. ---------------

  async function reconcileCart() {
    if (cartBusy || window.__o8CartLock.busy) return;
    setCartBusy(true);
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      for (const item of cart.items || []) {
        const allocation = item.selling_plan_allocation;
        const plan = allocation && allocation.selling_plan;
        if (!plan) continue;

        const weeks = parseWeeks(plan.name);
        if (!weeks) continue;

        const expectedBottles = weeks / 4;
        if (item.quantity === expectedBottles) continue;

        const plans = await fetchProductPlans(item.handle);
        if (!plans.length) continue;
        const snapped = snapToPlan(plans, item.quantity);
        if (String(snapped.planId) === String(plan.id) && snapped.bottles === item.quantity) continue;

        try {
          await postJSON('/cart/change.js', { id: item.key, quantity: 0 });
          await postJSON('/cart/add.js', {
            items: [{ id: item.variant_id, quantity: snapped.bottles, selling_plan: snapped.planId }],
          });
        } catch (err) {
          console.error('[o8-cart-guard] failed to reconcile line', item.key, err);
          continue;
        }

        if (window.location.pathname.replace(/\/$/, '') === '/cart') {
          window.location.reload();
        }
      }
    } finally {
      setCartBusy(false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileCart);
  } else {
    reconcileCart();
  }
})();